// Several sections include this file; run it only once so the class is not declared twice
if (!window.__busoCarouselScrollLoaded) {
  window.__busoCarouselScrollLoaded = true;

class BusoCarouselScroll {
  constructor(root) {
    this.root = root;
    this.wrapper =
      root.querySelector('[data-buso-carousel-track]') ||
      root.querySelector('.buso-carousel__track') ||
      root.querySelector('.slider');
    this.prevButton = root.querySelector('[data-buso-carousel-prev]');
    this.nextButton = root.querySelector('[data-buso-carousel-next]');
    this.dotsContainer = root.querySelector('[data-buso-carousel-dots]');
    this.itemSelector = root.dataset.itemSelector || '[data-buso-carousel-item]';
    this.mobileBreakpoint = parseInt(root.dataset.mobileBreakpoint || '750', 10);
    this.desktopSlidesPerScroll = parseInt(root.dataset.desktopSlides || '2', 10);

    if (!this.wrapper || !this.prevButton || !this.nextButton) return;

    this.bind();
    this.buildDots();
    this.updateControls();
  }

  isMobile() {
    return window.innerWidth < this.mobileBreakpoint;
  }

  getItems() {
    return Array.from(this.wrapper.querySelectorAll(this.itemSelector)).filter((item) => {
      return window.getComputedStyle(item).display !== 'none';
    });
  }

  getScrollAmount() {
    const items = this.getItems();
    const firstItem = items[0];
    if (!firstItem) return 0;

    const itemWidth = firstItem.offsetWidth;
    const gap = parseInt(getComputedStyle(this.wrapper).gap, 10) || 0;
    const slides = this.isMobile() ? 1 : this.desktopSlidesPerScroll;
    return (itemWidth + gap) * slides;
  }

  getMaxScroll() {
    return Math.max(0, this.wrapper.scrollWidth - this.wrapper.clientWidth);
  }

  /** One dot per arrow-click stop (not per card). */
  getPageCount() {
    const scrollAmount = this.getScrollAmount();
    const maxScroll = this.getMaxScroll();

    if (scrollAmount <= 0 || maxScroll <= 0) {
      return this.getItems().length > 1 ? 1 : 0;
    }

    return Math.floor(maxScroll / scrollAmount) + 1;
  }

  getActivePage() {
    const scrollAmount = this.getScrollAmount();
    if (scrollAmount <= 0) return 0;

    const maxPage = Math.max(0, this.getPageCount() - 1);
    const page = Math.round(this.wrapper.scrollLeft / scrollAmount);
    return Math.max(0, Math.min(page, maxPage));
  }

  updateDots() {
    if (!this.dotsContainer || !this.isMobile()) return;

    const dots = this.dotsContainer.querySelectorAll('.buso-carousel__dot');
    if (!dots.length) return;

    const activePage = this.getActivePage();
    dots.forEach((dot, index) => {
      const isActive = index === activePage;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.dotsContainer.setAttribute('aria-hidden', dots.length <= 1 ? 'true' : 'false');
  }

  buildDots() {
    if (!this.dotsContainer) return;

    if (this.dotsContainer.hasAttribute('data-buso-carousel-dots-prerendered')) {
      this.bindDots();
      return;
    }

    this.dotsContainer.innerHTML = '';

    if (!this.isMobile()) {
      this.dotsContainer.style.display = 'none';
      this.dotsContainer.setAttribute('aria-hidden', 'true');
      return;
    }

    const pageCount = this.getPageCount();

    if (pageCount <= 1) {
      this.dotsContainer.style.display = 'none';
      this.dotsContainer.setAttribute('aria-hidden', 'true');
      return;
    }

    this.dotsContainer.style.display = 'flex';
    this.dotsContainer.setAttribute('aria-hidden', 'false');

    for (let index = 0; index < pageCount; index++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'buso-carousel__dot' + (index === 0 ? ' is-active' : '');
      dot.dataset.page = index;
      dot.setAttribute('aria-label', 'Go to page ' + (index + 1));
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollToPage(index);
      });
      this.dotsContainer.appendChild(dot);
    }
  }

  bindDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.querySelectorAll('.buso-carousel__dot').forEach((dot) => {
      if (dot.dataset.bound === 'true') return;
      dot.dataset.bound = 'true';
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(dot.dataset.page ?? dot.dataset.slide ?? dot.dataset.index ?? '0', 10);
        this.scrollToPage(index);
      });
    });
  }

  scrollToPage(pageIndex) {
    const scrollAmount = this.getScrollAmount();
    const maxScroll = this.getMaxScroll();
    const targetScroll = Math.min(scrollAmount * pageIndex, maxScroll);

    this.wrapper.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }

  scrollToCard(direction) {
    const scrollAmount = this.getScrollAmount();
    const currentScroll = this.wrapper.scrollLeft;
    const maxScroll = this.getMaxScroll();

    let targetScroll;
    if (direction === 'next') {
      targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
    } else {
      targetScroll = Math.max(currentScroll - scrollAmount, 0);
    }

    this.wrapper.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }

  updateControls() {
    const scrollLeft = this.wrapper.scrollLeft;
    const maxScroll = this.getMaxScroll();
    const threshold = 10;

    this.prevButton.style.display = 'flex';
    this.nextButton.style.display = 'flex';
    this.prevButton.disabled = scrollLeft <= threshold;
    this.nextButton.disabled = scrollLeft >= maxScroll - threshold;

    this.updateDots();
  }

  bind() {
    this.prevHandler = () => this.scrollToCard('prev');
    this.nextHandler = () => this.scrollToCard('next');
    this.scrollHandler = () => this.updateControls();
    this.resizeHandler = () => {
      if (!this.dotsContainer?.hasAttribute('data-buso-carousel-dots-prerendered')) {
        this.buildDots();
      }
      this.updateControls();
      const maxScroll = this.getMaxScroll();
      if (this.wrapper.scrollLeft > maxScroll) {
        this.wrapper.scrollLeft = maxScroll;
      }
    };

    this.prevButton.addEventListener('click', this.prevHandler);
    this.nextButton.addEventListener('click', this.nextHandler);
    this.wrapper.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-buso-carousel]').forEach((root) => {
    if (root.dataset.busoCarouselInit === 'true') return;
    root.dataset.busoCarouselInit = 'true';
    new BusoCarouselScroll(root);
  });
});
}
